import { useState, useEffect } from "react";
import axios from "axios";
import logo from "./assets/logo.png";
import { Autocomplete, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import NotificationSnackbar from "./NotificationBar";

const WelcomePage = () => {
  const [selectedCompany, setSelectedCompany] = useState("");
  const [tin, setTin] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const navigate = useNavigate();

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get("/api/support/hiring-company");
      setCompanies(res.data);
    } catch (error) {
      console.error("Failed to load companies", error);
    }
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    setError("");
    let valid = true;

    if (!selectedCompany) {
      const msg = "Please select a company.";
      setError(msg);
      handleSnackbarOpen(msg, "error");
      valid = false;
    }

    if (!tin.trim()) {
      const msg = "Please enter your TIN number.";
      setError(msg);
      handleSnackbarOpen(msg, "error");
      valid = false;
    }

    if (!valid) return;

    setLoading(true);

    try {
      const response = await fetch("/api/auth/login-hiring-company", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: selectedCompany,
          tin: tin.trim(),
        }),
      });

      let data = null;
      const text = await response.text();

      try {
        data = text ? JSON.parse(text) : {};
      } catch (err) {
        console.error("RAW RESPONSE:", text);
        throw new Error("Invalid JSON response from server");
      }

      if (!response.ok) {
        const errorMessage = data?.message || `Error ${response.status}`;
        setError(errorMessage);
        handleSnackbarOpen(errorMessage, "error");
        return;
      }

      const successMessage = data?.message || "Login Successful!!";

      localStorage.setItem("isCompanyLoggedIn", "true");
      localStorage.setItem("companyData", JSON.stringify(data.company));

      handleSnackbarOpen(successMessage, "success");

      setTimeout(() => {
        navigate("/navigation-page", {
          state: { company: data.company },
        });
      }, 800);
    } catch (error) {
      console.error("Company login error:", error);

      let message = "Network error. Please try again.";

      if (error.message === "Invalid JSON response from server") {
        message = "Server returned an invalid response.";
      }

      setError(message);
      handleSnackbarOpen(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail.trim()) {
      handleSnackbarOpen("Please enter your email address.", "error");
      return;
    }
    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });

      const data = await response.json();
      if (!response.ok) {
        handleSnackbarOpen(data?.message || "Failed to send reset link.", "error");
        return;
      }

      handleSnackbarOpen(data?.message || "Reset link sent successfully!", "success");
      setResetModalOpen(false);
      setResetEmail("");
    } catch {
      handleSnackbarOpen("Network error. Please try again.", "error");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* LEFT PANEL */}
      <div
        style={{
          flex: 1,
          background: "linear-gradient(160deg, #0d3b66, #1a6fa8, #21a0d2)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          color: "#fff",
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 20 }}>
          <img
            src={logo}
            alt="Logo"
            style={{
              width: "80px",
              height: "80px",
              marginRight: "12px",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
            }}
          />
        </div>
        <h3
          style={{
            fontSize: 34,
            fontWeight: 800,
            margin: "0 0 12px",
            textAlign: "center",
            letterSpacing: "-0.5px",
          }}
        >
          RECRUITING COMPANY REPORTING PORTAL
        </h3>
        <div
          style={{
            marginTop: 48,
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {["Verified Company Logins", "Easy to Report", "Easy to Monitor"].map(
            (item) => (
              <div
                key={item}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  fontSize: 14,
                  opacity: 0.9,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                  }}
                >
                  ✓
                </div>
                {item}
              </div>
            ),
          )}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div
        style={{
          flex: 1,
          background: "#f4f7fb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 30px",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 20,
            padding: "44px 40px",
            width: "100%",
            maxWidth: 400,
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          }}
        >
          <>
            <h2
              style={{
                fontSize: 24,
                fontWeight: 800,
                color: "#0d3b66",
                margin: "0 0 4px",
              }}
            >
              Company Login
            </h2>
            <p style={{ color: "#888", fontSize: 13, marginBottom: 30 }}>
              Sign in to access your recruitment reporting
            </p>

            <div style={{ marginBottom: 20 }}>
              <div style={{ position: "relative" }}>
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#444",
                      marginBottom: 7,
                    }}
                  >
                    Company Name
                  </label>

                  <Autocomplete
                    options={companies}
                    getOptionLabel={(option) => option.name}
                    value={
                      companies.find((c) => c.name === selectedCompany) || null
                    }
                    onChange={(event, newValue) => {
                      setSelectedCompany(newValue ? newValue.name : "");
                      setError("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select your company"
                        sx={{
                          background: "#f8fafc",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            height: "48px",
                          },
                        }}
                      />
                    )}
                  />
                </div>

                <span
                  style={{
                    position: "absolute",
                    right: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    color: "#444",
                    fontSize: 12,
                  }}
                >
                  ▾
                </span>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#444",
                  marginBottom: 7,
                }}
              >
                Password
              </label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: "1.5px solid #dce3ec",
                  borderRadius: 10,
                  background: "#f8fafc",
                  padding: "0 14px",
                }}
              >
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="enter your TIN number"
                  value={tin}
                  onChange={(e) => {
                    setTin(e.target.value);
                    setError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "transparent",
                    padding: "12px 0",
                    fontSize: 14,
                    color: "#222",
                    outline: "none",
                    letterSpacing: 1,
                  }}
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 16,
                    padding: 0,
                  }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
            </div>

            {/* RESET PASSWORD LINK */}
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <span
                onClick={() => setResetModalOpen(true)}
                style={{
                  fontSize: 13,
                  color: "#1a6fa8",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Forgot password? Reset it
              </span>
            </div>

            {error && (
              <div
                style={{
                  background: "#fff4f4",
                  color: "#c0392b",
                  border: "1px solid #f5c6cb",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 13,
                  marginBottom: 18,
                }}
              >
                ⚠️ {error}
              </div>
            )}

            <button
              onClick={handleLogin}
              style={{
                width: "100%",
                padding: "14px",
                background: "linear-gradient(135deg, #0d3b66, #1a6fa8)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 6px 18px rgba(13,59,102,0.3)",
                letterSpacing: 0.4,
              }}
            >
              Login →
            </button>
          </>
        </div>
      </div>

      {/* RESET PASSWORD MODAL */}
{/* RESET PASSWORD MODAL */}
{resetModalOpen && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(13,59,102,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: 20,
    }}
  >
    <div
      style={{
        background: "#fff",
        borderRadius: 20,
        padding: "34px 32px",
        width: "100%",
        maxWidth: 380,
        boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        position: "relative",
      }}
    >
      {/* CLOSE ICON */}
      <IconButton
        onClick={() => {
          setResetModalOpen(false);
          setResetEmail("");
        }}
        size="small"
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          padding: "4px",
          color: "#666",
          "&:hover": {
            backgroundColor: "#f3f4f6",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <h2
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: "#0d3b66",
          margin: "0 0 4px",
        }}
      >
        Reset Password
      </h2>

      <p style={{ color: "#888", fontSize: 13, marginBottom: 25 }}>
        Enter your email and we'll send you a reset link.
      </p>

      {/* EMAIL */}
      <div style={{ marginBottom: 20 }}>
        <label
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: "#444",
            marginBottom: 7,
          }}
        >
          Email Address
        </label>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            border: "1.5px solid #dce3ec",
            borderRadius: 10,
            background: "#f8fafc",
            padding: "0 14px",
          }}
        >
          <input
            type="email"
            placeholder="Enter your email address"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
            style={{
              flex: 1,
              border: "none",
              background: "transparent",
              padding: "12px 0",
              fontSize: 14,
              color: "#222",
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* SUBMIT */}
      <button
        onClick={handleResetPassword}
        style={{
          width: "100%",
          padding: "14px",
          background: "linear-gradient(135deg, #0d3b66, #1a6fa8)",
          color: "#fff",
          border: "none",
          borderRadius: 12,
          fontSize: 15,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 6px 18px rgba(13,59,102,0.3)",
          letterSpacing: 0.4,
        }}
      >
        Submit →
      </button>
    </div>
  </div>
)}

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </div>
  );
};

export default WelcomePage;